<template>
  <div>
    <div
      v-if="selectedWarehouse && selectedWarehouse?.type !== 3"
      class="m-5 p-5 text-center bold-40"
    >
      {{ $t("order.thisWarehouseNotARetailType") }}
    </div>
    <div class="row gap-3" v-else>
      <div class="col">
        <router-link to="/sales/collects" class="btn btn-secondary btn-no-r">
          <span><BaseIcon name="active" /> {{ $t("order.collectList") }}</span>
        </router-link>
        <router-link
          to="/sales/products"
          class="btn btn-no-r btn-no-l btn-secondary"
        >
          <span><BaseIcon name="box" /> {{ $t("order.packingList") }}</span>
        </router-link>
        <router-link
          to="/sales/delivery-notes"
          class="btn btn-primary btn-no-r btn-no-l"
        >
          <span
            ><BaseIcon name="delivery-info" />
            {{ $t("salesCollectList.deliveryNotes") }}</span
          >
        </router-link>
        <router-link
          to="/sales/products/problems"
          class="btn btn-secondary btn-no-r btn-no-l"
        >
          <span
            ><BaseIcon name="box-error" /> {{ $t("order.ProblemList") }}</span
          >
        </router-link>
        <router-link to="/sales/return" class="btn btn-no-l btn-secondary">
          <span
            ><BaseIcon name="truck-remove" /> {{ $t("order.returnList") }}</span
          >
        </router-link>
      </div>
      <div class="col-auto d-flex gap-2 align-items-center">
        <button v-on:click="loadData" class="btn btn-type-2 btn-small">
          <span
            ><BaseIcon name="refresh" />
            {{ $t("salesCollectList.refresh") }}</span
          >
        </button>
      </div>
      <div class="col-12" v-if="notes === undefined">
        <div
          v-for="i in 3"
          :key="i"
          class="nature-white-box no-min-height mb-3"
        >
          <div class="row mt-3 mb-4 ms-2">
            <div class="col">
              <h2>
                <Skeletor :width="Math.random() * 21 + 30 + '%'" />
              </h2>
              <div>
                <span>
                  <Skeletor :width="Math.random() * 10 + 10 + '%'" />
                </span>
              </div>
            </div>
          </div>
          <Skeletor class="p-3 my-1" :width="Math.random() * 30 + 70 + '%'" />
          <Skeletor
            class="p-3 my-1"
            :width="Math.random() * 30 + 70 + '%'"
            v-if="Math.random() > 0.3"
          />
          <Skeletor
            class="p-3 my-1"
            :width="Math.random() * 30 + 70 + '%'"
            v-if="Math.random() > 0.6"
          />
          <Skeletor
            class="p-3 my-1"
            :width="Math.random() * 30 + 70 + '%'"
            v-if="Math.random() > 0.9"
          />
        </div>
      </div>
      <div v-else class="col-12">
        <div v-if="notes.length === 0" class="text-center mt-4">
          <Vue3Lottie
            :animationData="emptyJSON"
            :height="160"
            :width="300"
            :key="key"
          />
          <div class="bold-40 m-2 p-5">
            {{ $t("salesProductList.thereIsNothingToPack") }}
          </div>
        </div>

        <div v-for="day in groupedNotes" :key="day.name" class="day-group mb-5">
          <div class="d-flex align-items-center gap-3 mb-3 day-header">
            <div class="day-indicator"></div>
            <h3 class="m-0" v-if="getRelativeDateLabel(day.name)">
              {{ getRelativeDateLabel(day.name) }}
            </h3>
            <h3 class="m-0" v-else>
              {{ day.name }}
            </h3>
          </div>

          <div class="ps-2">
            <div
              v-for="courier in day.couriers"
              :key="courier.name"
              class="courier-group mb-4"
            >
              <div
                class="
                  d-flex
                  align-items-center
                  justify-content-between
                  mb-3
                  courier-header
                "
              >
                <div class="d-flex align-items-center gap-3 flex-grow-1">
                  <div class="courier-logo-wrapper">
                    <img
                      v-if="courier.logo"
                      :src="courier.logo"
                      class="courier-logo"
                    />
                    <BaseIcon v-else name="truck" class="text-muted" />
                  </div>
                  <h4 class="m-0 courier-name">{{ courier.name }}</h4>
                </div>
                <div class="courier-stats d-flex align-items-center gap-2">
                  <span class="stats-label"
                    >{{ $t("salesCollectList.deliveries") }}:</span
                  >
                  <span class="badge rounded-pill bg-primary px-3">
                    {{ courier.totalDeliveries }}
                  </span>
                </div>
              </div>

              <div class="">
                <div
                  v-for="app in courier.apps"
                  :key="app.id"
                  class="app-group"
                >
                  <div
                    class="nature-white-box no-min-height note-card"
                    v-for="note in app.notes"
                    :key="note.id"
                  >
                    <!-- SUMMARY HEADER -->
                    <div
                      class="
                        note-summary
                        d-flex
                        align-items-center
                        justify-content-between
                        p-3
                      "
                      @click="note.isOpen = !note.isOpen"
                    >
                      <div class="d-flex align-items-center gap-3 note-header">
                        <div
                          class="
                            d-flex
                            align-items-center
                            gap-2
                            pe-3
                            border-end
                          "
                        >
                          <img
                            v-if="app.logo"
                            :src="app.logo"
                            class="app-logo-small"
                          />
                          <span class="app-name-small text-muted fw-bold">
                            {{
                              app.name ||
                              $t("salesCollectList.appId") + ": " + app.id
                            }}
                          </span>
                        </div>
                        <div>
                          <div class="fw-bold">#{{ note.id }}</div>
                          <div v-if="note.created_at">
                            <div class="text-muted">
                              <div>{{ $t("salesCollectList.created") }}:</div>
                              <div>{{ formatDate(note.created_at, true) }}</div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <span
                            class="badge"
                            :class="
                              note.is_closed ? 'bg-success' : 'bg-warning'
                            "
                          >
                            {{
                              note.is_closed
                                ? $t("salesCollectList.closed")
                                : $t("salesCollectList.open")
                            }}
                          </span>
                          <div>
                            <div class="text-muted">
                              <div>
                                {{
                                  note.actual_pickup_date
                                    ? $t("salesCollectList.posted")
                                    : $t("salesCollectList.expected_posted")
                                }}:
                              </div>
                              <div>
                                {{
                                  note.actual_pickup_date
                                    ? formatDate(note.actual_pickup_date)
                                    : formatDate(note.planned_pickup_date)
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- ANALYTICS -->
                      <div class="analytics-card d-flex gap-3">
                        <!-- MAIN KPIS -->
                        <div
                          class="analytics-main d-flex justify-content-around"
                        >
                          <div class="analytics-item">
                            <div class="analytics-value">
                              {{ note.orderDeliveries.length }}
                            </div>
                            <div class="analytics-label">
                              {{ $t("salesCollectList.deliveries") }}
                            </div>
                          </div>
                          <div class="analytics-item">
                            <div class="analytics-value">
                              {{ getAvgDeliveryTime(note) || "-" }} d
                            </div>
                            <div class="analytics-label">
                              {{ $t("salesCollectList.avg_time") }}
                            </div>
                          </div>
                          <div class="analytics-item">
                            <div class="analytics-value">
                              <EditOnClick
                                :model-value="{
                                  amount: getExpectedCosts(note),
                                  currency: currencyCode,
                                }"
                                type="price"
                                :editable="false"
                              />
                            </div>
                            <div class="analytics-label">
                              {{ $t("salesCollectList.expectedCosts") }}
                            </div>
                          </div>
                        </div>

                        <!-- STATUS GROUP -->
                        <div
                          class="analytics-status d-flex justify-content-around"
                        >
                          <div class="status-item status-wait">
                            <div class="status-label">
                              {{ $t("deliveredStatuses.waitForDelivery") }}
                            </div>
                            <div class="status-value">
                              {{ note.analytics.wait }} ({{
                                note.analytics.wait_pct
                              }}%)
                            </div>
                          </div>
                          <div class="status-item status-under">
                            <div class="status-label">
                              {{ $t("deliveredStatuses.underDelivery") }}
                            </div>
                            <div class="status-value">
                              {{ note.analytics.under_delivery }} ({{
                                note.analytics.under_delivery_pct
                              }}%)
                            </div>
                          </div>
                          <div class="status-item status-refused">
                            <div class="status-label">
                              {{ $t("deliveredStatuses.failed") }}
                            </div>
                            <div class="status-value">
                              {{ note.analytics.refused }} ({{
                                note.analytics.refused_pct
                              }}%)
                            </div>
                          </div>
                          <div class="status-item status-delivered">
                            <div class="status-label">
                              {{ $t("deliveredStatuses.delivered") }}
                            </div>
                            <div class="status-value">
                              {{ note.analytics.delivered }} ({{
                                note.analytics.delivered_pct
                              }}%)
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- ACTIONS -->
                      <div class="d-flex align-items-center gap-2">
                        <button
                          class="btn btn-success btn-sm"
                          @click.stop="printNote(note)"
                          v-if="note.is_closed || !note.has_printable_notes"
                        >
                          <BaseIcon name="printer" />
                        </button>

                        <button
                          class="btn btn-primary btn-sm"
                          @click.stop="closeNote(note)"
                          v-else
                        >
                          <BaseIcon name="box-add" />
                        </button>

                        <button
                          class="btn btn-link p-2 variants-btn"
                          data-bs-placement="top"
                          title="Show"
                          :class="{ open: note.isOpen }"
                        >
                          <BaseIcon name="bottom-arrow" />
                        </button>
                      </div>
                    </div>

                    <!-- EXPANDED CONTENT -->
                    <transition name="fade-slide">
                      <div v-if="note.isOpen" class="p-3 border-top">
                        <one-by-one-table
                          v-model:rows="rows"
                          v-model:items="note.orderDeliveries"
                          :show-add="false"
                          :show-edit="false"
                          :show-del="false"
                          type="deliveries"
                        />
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import OneByOneTable from "../../components/lists/OneByOneTable";
import http from "@/modules/http";
import BaseIcon from "@/components/icons/BaseIcon";
import ws from "@/modules/ws";
import { useStore } from "vuex";
import deliveredStatuses from "@/modules/DeliveredStatuses";
import { Vue3Lottie } from "vue3-lottie";
import emptyJSON from "../../assets/animations/ghost.json";
import date from "../../modules/date";
import printJS from "print-js";
import tenant from "../../modules/tenant";
import { defineAsyncComponent } from "vue";
import EditOnClick from "../../components/inputs/EditOnClick.vue";

export default {
  name: "SalesDeliveryNotes",
  components: { EditOnClick, Vue3Lottie, BaseIcon, OneByOneTable },
  data() {
    return {
      store: useStore(),
      deliveredStatuses: deliveredStatuses,
      emptyJSON: emptyJSON,
      currencyCode: null,
      rows: [
        {
          name: "Tracking code",
          key: "public_tracking_code",
          type: "string",
          show: true,
          editable: false,
        },
        {
          name: "Type",
          show: true,
          editable: false,
          key: "type",
          type: "enum",
          label: "name",
          values: {
            1: { id: 1, name: "Normal delivery" },
            2: { id: 2, name: "Pick-up point" },
            3: { id: 3, name: "Return" },
            4: { id: 4, name: "Change" },
            5: { id: 5, name: "Next day delivery" },
            6: { id: 6, name: "Saturday delivery" },
            7: { id: 7, name: "Flexible day delivery" },
            8: { id: 8, name: "Timeslot delivery" },
            9: { id: 9, name: "Drop off return" },
          },
        },
        {
          name: "Delivery to",
          key: "city",
          type: "string",
          show: true,
          editable: false,
        },
        {
          name: "Weight",
          key: "weight",
          type: "number",
          show: true,
          editable: false,
          postfix: "g",
        },
        {
          name: "Status",
          key: "status",
          type: "object",
          component: defineAsyncComponent(() =>
            import("../../components/lists/listItems/DeliveryStatusBar")
          ),
          show: true,
          editable: false,
        },
      ],
      notes: undefined,
      itemsById: {},
    };
  },
  computed: {
    warehouse() {
      return this.store.state.topBar.warehouse;
    },
    warehouses() {
      return this.store.state.warehouses;
    },
    selectedWarehouse() {
      return this.warehouses.find((e) => e.id === this.warehouse);
    },
    q() {
      return this.store.state.topBar.q;
    },
    sidebarFilters() {
      return this.store.state.topBar.sidebarFilters;
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    storageUrl() {
      return tenant.getAppFilesUrl();
    },
    groupedNotes() {
      if (!this.notes) return [];

      const groups = {};

      this.notes.forEach((note) => {
        const day = this.formatDate(note.planned_pickup_date);
        const courierKey = note.courier_code || "Unknown";
        const courierName = note.name || courierKey;
        const appKey = note.app_id;

        if (!groups[day]) {
          groups[day] = { name: day, couriers: {}, isOpen: true };
        }

        if (!groups[day].couriers[courierKey]) {
          groups[day].couriers[courierKey] = {
            name: courierName,
            logo: note.logo,
            apps: {},
            isOpen: true,
            totalDeliveries: 0,
          };
        }
        if (!groups[day].couriers[courierKey].apps[appKey]) {
          groups[day].couriers[courierKey].apps[appKey] = {
            id: appKey,
            name: note.app_name,
            logo: note.app_logo,
            notes: [],
            isOpen: true,
          };
        }

        groups[day].couriers[courierKey].totalDeliveries +=
          note.orderDeliveries.length;
        groups[day].couriers[courierKey].apps[appKey].notes.push(note);
      });

      // Convert objects to sorted arrays
      return Object.values(groups)
        .sort((a, b) => new Date(b.name) - new Date(a.name))
        .map((dayGroup) => ({
          ...dayGroup,
          couriers: Object.values(dayGroup.couriers)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((courierGroup) => ({
              ...courierGroup,
              apps: Object.values(courierGroup.apps).sort((a, b) =>
                (a.name || "").localeCompare(b.name || "")
              ),
            })),
        }));
    },
  },
  watch: {
    warehouse() {
      this.loadData();
    },
    q() {
      this.loadData();
    },
    sidebarFilters: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
  },
  mounted() {
    ws.init();

    ws.subscribe("modify", "deliveryNote", (e) => {
      if (
        e.rowKey === "collected" ||
        e.rowKey === "in_package" ||
        e.rowKey === "quantity" ||
        e.rowKey === "returned" ||
        e.rowKey === "need_to_sent_quantity"
      ) {
        let obj = this.itemsById[e.objectId];
        if (obj !== undefined) {
          obj[e.rowKey] = e.value;
        }
      }
    });

    this.loadData();
  },
  methods: {
    getRelativeDateLabel(dateStr) {
      if (!dateStr) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // We use a regex to ensure we parse the date part correctly without timezone shift issues
      // dateStr is expected to be YYYY-MM-DD
      const parts = dateStr.split("-");
      if (parts.length !== 3) return null;

      const target = new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2])
      );
      target.setHours(0, 0, 0, 0);

      if (target.getTime() === today.getTime()) {
        return this.$t("salesCollectList.today");
      }
      if (target.getTime() === tomorrow.getTime()) {
        return this.$t("salesCollectList.tomorrow");
      }

      // Next Monday logic
      // If today is Friday, Saturday or Sunday, and target is the coming Monday
      const dayOfWeek = today.getDay(); // 0 (Sun) to 6 (Sat)
      if (dayOfWeek >= 5 || dayOfWeek === 0) {
        const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + daysUntilMonday);

        if (target.getTime() === nextMonday.getTime()) {
          return this.$t("salesCollectList.nextMonday");
        }
      }

      return null;
    },
    formatDate(val, withTime = false) {
      return date.format(val, false, withTime);
    },
    getAvgDeliveryTime(note) {
      let sum = 0;
      const times = note.orderDeliveries
        .map((d) => {
          const start = new Date(d.tracking_code_created_at);
          const end = new Date(d.drop_off_at || d.delivered_at);
          if (!d.tracking_code_created_at || !(d.drop_off_at || d.delivered_at))
            return null;
          const val = (end - start) / (1000 * 60 * 60 * 24);
          sum += val;
          return val;
        })
        .filter(Boolean);

      if (!times.length) return null;
      return Math.round((sum / times.length) * 10) / 10;
    },
    getExpectedCosts(note) {
      let sum = 0;
      for (const delivery of note.orderDeliveries) {
        sum += (delivery?.estimated_cost ?? 0) - 0;
      }
      return Math.round(sum * 100) / 100;
    },
    getParcelStatusBreakdown(note) {
      const total = note.orderDeliveries.length;
      const breakdown = {
        wait: 0,
        under_delivery: 0,
        refused: 0,
        delivered: 0,
      };

      note.orderDeliveries.forEach((d) => {
        const status = d.status?.status;
        if (status < 3) {
          breakdown.wait++;
        } else if (d.refused_at) {
          breakdown.refused++;
        } else if (d.delivered_at || d.drop_off_at) {
          breakdown.delivered++;
        } else {
          breakdown.under_delivery++;
        }
      });

      // convert to %
      Object.keys(breakdown).forEach((k) => {
        breakdown[k + "_pct"] = total
          ? ((breakdown[k] / total) * 100).toFixed(0)
          : 0;
      });

      return breakdown;
    },
    loadData() {
      this.notes = undefined;
      let warehouse = this.warehouse;
      if (this.warehouse === undefined || this.warehouse === null) {
        warehouse = "";
      }
      let url = `/delivery/notes?warehouse=${warehouse}&q=${this.q || ""}`;
      if (this.sidebarFilters?.from && this.sidebarFilters?.to) {
        url += `&from=${this.sidebarFilters.from}&to=${this.sidebarFilters.to}`;
      }
      http.fetch(url).then((data) => {
        this.currencyCode = data.currency_code;
        this.notes = data.data.map((n) => ({
          ...n,
          analytics: this.getParcelStatusBreakdown(n),
          isOpen: false,
          loadPrintBtn: false,
          loadCloseBtn: false,
        }));
        this.itemsById = {};
        for (const key in this.notes) {
          this.itemsById[this.notes[key].id] = this.notes[key];
          if (
            this.notes[key].logo !== null &&
            this.notes[key].logo.indexOf("http") !== 0 &&
            this.notes[key].logo.indexOf("//") !== 0
          ) {
            this.notes[key].logo = this.storageUrl + "/" + this.notes[key].logo;
          }
          if (
            this.notes[key].app_logo !== null &&
            this.notes[key].app_logo.indexOf("http") !== 0 &&
            this.notes[key].app_logo.indexOf("//") !== 0
          ) {
            this.notes[key].app_logo =
              this.storageUrl + "/" + this.notes[key].app_logo;
          }
        }
      });
    },
    closeNote(note) {
      note.loadCloseBtn = true;
      http.fetch(`/delivery/notes/close`, { id: note.id }).then(() => {
        note.loadCloseBtn = false;
        this.itemsById[note.id].is_closed = true;
      });
    },
    printNote(note) {
      note.loadPrintBtn = true;

      let url = `${tenant.getApiBaseUrl()}/delivery/notes/${
        note.id
      }/print/?_token=${this.store.state.token}`;

      if (navigator.userAgent.toLowerCase().indexOf("firefox") > -1) {
        window.open(url, "_blank");
        note.loadPrintBtn = false;
      } else {
        printJS({
          printable: url,
          onLoadingEnd: function () {
            note.loadPrintBtn = false;
          },
        });
      }
    },
  },
};
</script>
<style scoped>
/* MAIN KPIS GROUP */
.analytics-main {
  display: flex;
  justify-content: space-around;
  text-align: center;
}

.analytics-main .analytics-item {
  flex: 1;
}

.analytics-value {
  font-weight: 600;
  font-size: 1.2rem;
  color: #212529;
}

.analytics-label {
  font-size: 0.85rem;
  color: #6c757d;
}

/* STATUS GROUP */
.analytics-status {
  display: flex;
  justify-content: space-around;
  text-align: center;
  gap: 1rem;
}

/* Optional: colored badges or subtle backgrounds for each status */
.status-item {
  flex: 1;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background-color: #f8f9fa; /* light gray */
}

.status-wait {
  background-color: #fff3cd; /* yellow */
}

.status-under {
  background-color: #cce5ff; /* blue */
}

.status-refused {
  background-color: #f8d7da; /* red */
}

.status-delivered {
  background-color: #d4edda; /* green */
}

.status-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.85rem;
}

.status-value {
  font-weight: 600;
  font-size: 1rem;
  margin-top: 0.25rem;
}

.analytics-card {
  flex: 1 1 0;
  justify-content: space-around;
}

.note-header {
  width: 400px;
}

.cursor-pointer {
  cursor: pointer;
}

.group-header {
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.day-group {
  position: relative;
}

.day-header {
  padding: 0.5rem 0;
}

.day-indicator {
  width: 4px;
  height: 24px;
  background-color: #0d6efd;
  border-radius: 2px;
}

.day-header h3 {
  font-weight: 700;
  color: #212529;
  letter-spacing: -0.02em;
}

.courier-group {
  background-color: #f8f9fa;
  border-radius: 0.75rem;
  border: 1px solid #edf2f7;
  overflow: hidden;
}

.courier-header {
  padding: 0.75rem 1rem;
  background-color: #ffffff;
  border-bottom: 1px solid #edf2f7;
}

.courier-logo-wrapper {
  width: 40px;
  height: 40px;
  background-color: #fff;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.courier-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.courier-name {
  font-weight: 600;
  color: #4a5568;
}

.stats-label {
  font-size: 0.8rem;
  color: #718096;
  text-transform: uppercase;
  font-weight: 600;
}

.app-logo-small {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: contain;
}

.app-name-small {
  font-size: 0.75rem;
  white-space: nowrap;
}

.note-card {
  margin: 0.75rem;
  border-left: 4px solid #0d6efd;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  overflow: hidden;
  background-color: #ffffff;
}

.note-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.rotate-270 {
  transform: rotate(-90deg);
}
</style>
